import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "./i18n";
import { track } from "./analytics";
import "./ChapterNav.css";

/**
 * ChapterNav — floating chapter dots on the homepage (desktop) so visitors can
 * see where they are and jump between the major chapters.
 * MobilePickupCTA — a persistent, unobtrusive "Free Pickup" button on phones.
 */

const CHAPTERS: { id: string; label: { en: string; es: string } }[] = [
  { id: "top", label: { en: "Top", es: "Inicio" } },
  { id: "how", label: { en: "How it works", es: "Cómo funciona" } },
  { id: "services", label: { en: "Who we serve", es: "A quién servimos" } },
  { id: "impact", label: { en: "Impact", es: "Impacto" } },
  { id: "business", label: { en: "For business", es: "Para negocios" } },
  { id: "journey", label: { en: "Item journey", es: "El viaje" } },
  { id: "founder", label: { en: "Founders", es: "Fundadores" } },
  { id: "ecoheroes", label: { en: "EcoVerse", es: "EcoVerse" } },
  { id: "faq", label: { en: "FAQ", es: "Preguntas" } },
];

export const ChapterNav: React.FC = () => {
  const { lang } = useLang();
  const [active, setActive] = useState("top");

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -50% 0px" }
    );
    CHAPTERS.forEach((c) => {
      if (c.id === "top") return;
      const el = document.getElementById(c.id);
      if (el) io.observe(el);
    });
    const onScroll = () => {
      if (window.scrollY < window.innerHeight * 0.5) setActive("top");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const jump = (id: string) => {
    if (id === "top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav className="cn-nav" aria-label={lang === "es" ? "Capítulos de la página" : "Page chapters"}>
      {CHAPTERS.map((c) => (
        <button
          key={c.id}
          className={`cn-dot${active === c.id ? " on" : ""}`}
          onClick={() => jump(c.id)}
          aria-label={c.label[lang]}
          title={c.label[lang]}
        >
          <span className="cn-tip">{c.label[lang]}</span>
        </button>
      ))}
    </nav>
  );
};

export const MobilePickupCTA: React.FC = () => {
  const { lang } = useLang();
  const location = useLocation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Pages with their own flow or immersion don't need the floating CTA.
  if (["/pickup", "/experience", "/game", "/business-quote"].includes(location.pathname)) return null;

  return (
    <Link
      to="/pickup"
      className={`cn-mobile-cta${show ? " show" : ""}`}
      onClick={() => track("pickup_cta_clicked", { source: "floating_mobile" })}
    >
      📦 {lang === "es" ? "Recogida Gratis" : "Free Pickup"}
    </Link>
  );
};

export default ChapterNav;
